import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Breadcrumbs,
  Link,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link as RouterLink } from 'react-router-dom';
import SeoHead from '../components/common/SeoHead';
import LazyImage from '../components/common/LazyImage';

// 导入图片
import purpleAmethyst from '../assets/images/index/cuR2.jpg';
import clearQuartz from '../assets/images/index/cuR1.png';
import roseQuartz from '../assets/images/index/cuR3.png';
import blackObsidian from '../assets/images/index/cuR4.jpg';
import jadeCrystal from '../assets/images/index/cuR5.jpg';

const CrystalGuide = () => {
  // 结构化数据
  const crystalGuideSchemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "水晶百科：不同水晶的能量特性与功效全解析",
    "description": "详细介绍常见水晶的能量属性、功效与选择指南。了解紫水晶、白水晶、粉晶、黑曜石等水晶如何帮助平衡能量、增强运势，提升生活品质。",
    "image": "https://destiny-crystal.com/assets/images/index/cuR1.png",
    "author": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶"
    },
    "publisher": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶",
      "logo": {
        "@type": "ImageObject",
        "url": "https://destiny-crystal.com/assets/images/logo.png"
      }
    },
    "datePublished": "2025-03-26",
    "dateModified": "2025-03-26"
  };

  const crystals = [
    {
      id: 'amethyst',
      name: '紫水晶',
      englishName: 'Amethyst',
      image: purpleAmethyst,
      element: '风',
      chakra: '第三眼轮、顶轮',
      effects: ['净化心灵', '提升灵性意识', '缓解压力', '增强直觉'],
      description: '紫水晶是一种紫色石英晶体，被视为"灵性之石"。它能净化负面能量，促进冥想与灵性成长，也有助于改善睡眠质量，减轻压力与焦虑。紫水晶与第三眼轮和顶轮相连，可以打开更高层次的意识和洞察力。',
      compatibility: ['白水晶', '紫黄晶', '月光石'],
      maintenance: '避免长时间阳光直射，可用月光净化，或放置于白水晶簇上充能。'
    },
    {
      id: 'clear-quartz',
      name: '白水晶',
      englishName: 'Clear Quartz',
      image: clearQuartz,
      element: '风、水',
      chakra: '所有脉轮',
      effects: ['放大能量', '净化', '提升意识', '增强记忆力'],
      description: '白水晶被称为"水晶之王"，是最多功能的水晶之一。它能放大其他水晶的能量，净化空间，提升意识层次。白水晶与所有脉轮相连，有助于平衡整体能量场。它也被认为可以增强记忆力、集中力和清晰思考能力。',
      compatibility: ['任何水晶都可搭配'],
      maintenance: '定期用流水冲洗，可放在阳光或月光下净化充能。'
    },
    {
      id: 'rose-quartz',
      name: '粉晶',
      englishName: 'Rose Quartz',
      image: roseQuartz,
      element: '水',
      chakra: '心轮',
      effects: ['促进爱与和谐', '治愈情感伤痛', '增强人际关系', '自我接纳'],
      description: '粉晶是爱的象征，被称为"爱情石"。它能促进无条件的爱、自爱和对他人的慈悲。粉晶与心轮相连，有助于治愈情感伤痛，吸引健康的爱情关系，并创造和谐的家庭环境。它的温柔能量也有助于减轻压力和焦虑。',
      compatibility: ['绿幽灵', '绿发晶', '海蓝宝'],
      maintenance: '避免长时间阳光直射，建议用月光净化或轻柔的流水冲洗。'
    },
    {
      id: 'black-obsidian',
      name: '黑曜石',
      englishName: 'Black Obsidian',
      image: blackObsidian,
      element: '土',
      chakra: '根轮',
      effects: ['保护', '接地', '净化负能量', '真相揭示'],
      description: '黑曜石是一种强大的保护石，能阻挡负面能量并将其转化。它与根轮相连，提供稳定的接地能量，帮助我们连接到地球。黑曜石也被称为"真相之石"，能揭示隐藏的真相，带来清晰的洞察力。它特别适合用于灵性保护和清除负能量场。',
      compatibility: ['红玛瑙', '石榴石', '赤铁矿'],
      maintenance: '定期用流水冲洗，并可放在土中短暂埋藏来净化。'
    },
    {
      id: 'jade',
      name: '翡翠',
      englishName: 'Jade',
      image: jadeCrystal,
      element: '木',
      chakra: '心轮',
      effects: ['和谐与平衡', '丰盛与繁荣', '长寿', '保护'],
      description: '翡翠在东方文化中被视为最珍贵的宝石之一，象征着纯洁、和谐与智慧。它能促进内心和谐与平衡，吸引丰盛与繁荣。翡翠与心轮相连，鼓励同情心和智慧，也被认为能保护佩戴者免受负面能量侵扰。在中国传统中，翡翠也被视为长寿与健康的象征。',
      compatibility: ['绿幽灵', '绿发晶', '绿东陵'],
      maintenance: '避免化学品接触，可用柔软干布轻轻擦拭，避免长时间阳光直射。'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* SEO优化 */}
      <SeoHead
        title="水晶百科：能量水晶的功效与选择指南 | DestinyCrystal"
        description="详细介绍常见水晶的能量属性、功效与选择指南。了解紫水晶、白水晶、粉晶、黑曜石等水晶如何帮助平衡能量、增强运势，提升生活品质。"
        canonicalUrl="https://destiny-crystal.com/crystal-guide"
        imageUrl="https://destiny-crystal.com/assets/images/index/cuR1.png"
        keywords={["水晶百科", "水晶功效", "紫水晶", "白水晶", "粉晶", "黑曜石", "翡翠", "能量水晶", "水晶选择"]}
        schemaData={crystalGuideSchemaData}
      />

      {/* 面包屑导航 */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" color="inherit">
          首页
        </Link>
        <Typography color="text.primary">水晶百科</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 1 }}>
        水晶百科
      </Typography>
      <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 5 }}>
        探索不同水晶的能量特性与功效
      </Typography>

      {/* 介绍部分 */}
      <Paper elevation={0} sx={{ p: 3, mb: 6, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="body1" paragraph>
          水晶被认为拥有独特的能量振动和特性，人类几千年来一直用它们来促进身心灵的健康。每种水晶都有其独特的能量特性，可以用于不同的目的，如增强爱的能量、提升保护力、促进财富、增强直觉或平衡脉轮。
        </Typography>
        <Typography variant="body1" paragraph>
          在选择水晶时，建议遵循您的直觉。许多人相信我们被我们最需要的水晶所吸引。水晶可以通过佩戴、放置在特定区域、用于冥想或创建水晶网格来使用。为了保持水晶的最佳能量状态，定期清洁和充能是很重要的。
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          如何使用本指南
        </Typography>
        <Typography variant="body2" color="text.secondary">
          浏览下方的水晶库，了解每种水晶的能量特性、功效和使用方法。点击每个水晶卡片可以展开查看更详细的信息。您也可以直接前往我们的商城，选购心仪的水晶产品。
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label="按能量分类" 
            color="primary" 
            variant="outlined" 
            sx={{ fontWeight: 'medium' }}
          />
          <Chip 
            label="按脉轮系统分类" 
            color="secondary" 
            variant="outlined" 
            sx={{ fontWeight: 'medium' }}
          />
          <Chip 
            label="最受欢迎" 
            color="success" 
            variant="outlined" 
            sx={{ fontWeight: 'medium' }}
          />
        </Box>
      </Paper>

      {/* 水晶列表 */}
      <Grid container spacing={4}>
        {crystals.map((crystal) => (
          <Grid item xs={12} key={crystal.id}>
            <Accordion sx={{ 
              overflow: 'hidden',
              borderRadius: '12px !important',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 }
            }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="primary" />}
                aria-controls={`${crystal.id}-content`}
                id={`${crystal.id}-header`}
                sx={{ 
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  px: 3
                }}
              >
                <Grid container alignItems="center" spacing={3}>
                  <Grid item xs={12} sm={3} md={2}>
                    <Box sx={{ 
                      width: '100%', 
                      height: { xs: 160, sm: 100 }, 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}>
                      <LazyImage 
                        src={crystal.image} 
                        alt={crystal.name} 
                        sx={{ borderRadius: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={9} md={10}>
                    <Typography variant="h6" component="div">
                      {crystal.name} <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>({crystal.englishName})</span>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {crystal.effects.join(' · ')}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip 
                        label={`元素: ${crystal.element}`} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontWeight: 'medium' }}
                      />
                      <Chip 
                        label={`脉轮: ${crystal.chakra}`} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontWeight: 'medium' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </AccordionSummary>
              
              <AccordionDetails sx={{ px: 3, pt: 3, pb: 4 }}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography paragraph>
                      {crystal.description}
                    </Typography>
                    
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        主要功效
                      </Typography>
                      <List disablePadding>
                        {crystal.effects.map((effect, idx) => (
                          <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={effect} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                    
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        搭配建议
                      </Typography>
                      <Typography variant="body2">
                        {crystal.name}可与{crystal.compatibility.join('、')}等水晶搭配使用，产生协同效应，增强整体能量。
                      </Typography>
                    </Box>
                    
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        保养方法
                      </Typography>
                      <Typography variant="body2">
                        {crystal.maintenance}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Link 
                        component={RouterLink} 
                        to="/shop" 
                        underline="none" 
                        sx={{ 
                          display: 'inline-block',
                          bgcolor: 'primary.main',
                          color: 'white',
                          py: 1,
                          px: 3,
                          borderRadius: 2,
                          fontWeight: 'medium',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          }
                        }}
                      >
                        浏览{crystal.name}商品
                      </Link>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      {/* 用法指南 */}
      <Paper sx={{ p: 4, mt: 6, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom align="center">
          水晶使用与保养指南
        </Typography>
        
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              水晶的使用方法
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="随身佩戴"
                  secondary="将水晶做成手链、项链等随身佩戴，能持续接收其能量"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="冥想伴侣"
                  secondary="冥想时手持或放置在身体特定部位，增强冥想效果"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="家居摆放"
                  secondary="放置在家中或办公室的特定位置，平衡环境能量"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="创建水晶网格"
                  secondary="多种水晶组合成特定图案，产生协同效应"
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              水晶的保养方法
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="净化方法"
                  secondary="月光浴、流水冲洗、白水晶簇上放置、烟熏净化等"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="充能方法"
                  secondary="阳光浴（注意部分水晶不适合）、月光浴、埋入土中、意念充能"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="日常维护"
                  secondary="避免化学品接触、防止剧烈碰撞、定期清洁、柔软布料擦拭"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="储存建议"
                  secondary="单独包裹存放、避免与硬度较高水晶接触、避免长期阳光直射"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* 底部号召性按钮 */}
      <Box sx={{ textAlign: 'center', mt: 8, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          找到适合您的能量水晶
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          探索我们精心挑选的高品质水晶手串，每款水晶都经过专业净化与加持，助您平衡能量、增强运势。
        </Typography>
        <Link
          component={RouterLink}
          to="/shop"
          underline="none"
          sx={{
            display: 'inline-block',
            bgcolor: 'primary.main',
            color: 'white',
            py: 1.5,
            px: 4,
            borderRadius: 2,
            fontWeight: 'medium',
            '&:hover': {
              bgcolor: 'primary.dark',
            }
          }}
        >
          浏览我们的水晶商品
        </Link>
      </Box>
    </Container>
  );
};

export default CrystalGuide;